<script lang="ts">
	import { checkPermissions, getOmniVersion } from '$lib/audio';
	import { assistantDerived } from '$lib/memory.store';
	import type { TranscriptFull } from '@repo/common/types/Transcript';
	import { chatServiceHistory } from '@repo/sharedlib/src/chat.service.store';
	import * as AuthStore from '@repo/sharedlib/src/stores/auth.store';
	import * as CalendarStore from '@repo/sharedlib/src/stores/calendar.store';
	import * as CalendarEventStore from '@repo/sharedlib/src/stores/calendarEvent.store';
	import * as ConfigStore from '@repo/sharedlib/src/stores/config';
	import * as ConversationStore from '@repo/sharedlib/src/stores/conversation.store';
	import * as ResearchStore from '@repo/sharedlib/src/stores/conversation.store';
	import * as DevicesStore from '@repo/sharedlib/src/stores/devices';
	import * as LastUsedDeviceStore from '@repo/sharedlib/src/stores/last.devices.store';
	import * as NavigationStore from '@repo/sharedlib/src/stores/nav.store';
	import * as OnboardingStore from '@repo/sharedlib/src/stores/onboarding.store';
	import * as ProvidersStore from '@repo/sharedlib/src/stores/provider.calendars.store';
	import * as SelectedCalendarsStore from '@repo/sharedlib/src/stores/selected.calendars.store';
	import * as SidebarStore from '@repo/sharedlib/src/stores/sidebar.store';
	import * as TaskStore from '@repo/sharedlib/src/stores/task.store';
	import * as DraftFlowStores from '@repo/sharedlib/src/stores/draftFlow.store';
	import * as FilteredTasksStores from '@repo/sharedlib/src/stores/filter.store';
	import * as ExportLinearDialogStore from '@repo/sharedlib/src/stores/exportLinearDialog.store';
	import * as LinearProviderStore from '@repo/sharedlib/src/stores/provider.linear.store';

	import * as ThreadsStore from '@repo/sharedlib/src/stores/threads.store';
	import * as TaskSynthStore from '@repo/sharedlib/src/stores/taskSynth.store';
	import { transcriptsView } from '@repo/sharedlib/src/stores/transcripts';
	import {
		recordedTranscripts,
		transcriptOutput,
		transcripts
	} from '@repo/sharedlib/src/stores/transcripts';
	import {
		getEventStatistics,
		uiCalendarDayEvents,
		uiCalendarEvents,
		uiCalendarEventsByDate,
		uiCalendarEventsBySection,
		uiCalendarMonthEvents,
		uiCalendarNavEvents,
		uiCalendarOverviewEvents,
		uiCalendarStoreState,
		uiCalendarWeekEvents
	} from '@repo/sharedlib/src/stores/ui-calendar-events/index';
	import * as ViewerStore from '@repo/sharedlib/src/stores/viewer';
	import * as ViewersStore from '@repo/sharedlib/src/stores/viewer.store';
	import * as WelcomeStore from '@repo/sharedlib/src/stores/welcome.store';
	import { workspaceStore } from '@repo/sharedlib/src/stores/workspace.store';
	import { emailContacts } from '@repo/sharedlib/src/stores/emailContacts.store';
	import { createMemoryFromVtt, getTranscriptName } from '@repo/toolslib/src/utils/transcript.util';
	import { currentDraft } from '@repo/ui/src/stores/draft.store';
	import Button from '@ui-components/ui/Button.svelte';
	import { JsonView } from '@zerodevx/svelte-json-view';
	import highlightWords from 'highlight-words';
	import { get, type Readable, writable } from 'svelte/store';
	import { Hammer } from '../Exchange/icons';

	let showDebugPanel = $state(false);
	let searchQuery = $state('');
	let selectedData = $state('onboarding');
	let showVttModal = $state(false);
	let vttInput = $state('');
	let vttError = $state('');

	// Handle VTT submission
	async function handleVttSubmit() {
		try {
			if (!vttInput.trim()) {
				vttError = 'Please enter VTT content';
				return;
			}

			const memory = createMemoryFromVtt(
				vttInput,
				get(workspaceStore)?.lastSelectedWorkspace || undefined,
				get(AuthStore.user)?.id || 'debug-user'
			);
			if (!memory) {
				vttError =
					'Failed to parse VTT. Check console for details. Valid format: WEBVTT\\n\\nHH:MM:SS.mmm --> HH:MM:SS.mmm\\nText content';
				console.error('[VTT Import Error] VTT parsing failed for input:', vttInput);
				return;
			}

			const transcriptFull = memory.transcript as TranscriptFull;

			// Step 1: Create transcript in database with status='ready' (since we have full output)
			console.log('[VTT Import] Creating transcript in database...');
			const created = await transcripts.createFromData(
				undefined,
				undefined,
				transcriptFull.duration,
				'transcribed',
				transcriptFull.workspaceId
			);

			console.log('[VTT Import] Created transcript:', created.id);

			// Step 2: Update transcript with parsed VTT data
			console.log('[VTT Import] Updating transcript with VTT data...');

			// Convert the parsed output to the bracket format that the system expects
			const bracketFormat =
				transcriptFull.output?.map((line) => `[${line.vtt}] ${line.text}`) || [];

			console.log('[VTT Import] Bracket format (array):', bracketFormat);
			console.log('[VTT Import] Bracket format length:', bracketFormat.length);
			bracketFormat.forEach((line, idx) => {
				console.log(`[VTT Import] Line ${idx}:`, line);
			});

			await transcripts.updateTranscript(
				created.id,
				{
					name: 'Manual VTT Transcript',
					rawOutput: bracketFormat,
					editedOutput: bracketFormat,
					output: transcriptFull.output
				},
				true,
				true
			);

			console.log('[VTT Import Success] Transcript ID:', created.id);
			console.log('[VTT Import Success] Transcript Output:', transcriptFull.output);
			console.log('[VTT Import Success] Raw output saved:', bracketFormat);

			vttInput = '';
			showVttModal = false;
			vttError = '';
			alert(
				`Memory created and saved!\n\nTranscript ID: ${created.id}\nLines: ${transcriptFull.output?.length || 0}\n\nCheck console for details.`
			);
		} catch (error) {
			console.error('[VTT Import Error]', error);
			vttError = error instanceof Error ? error.message : 'Unknown error';
		}
	}
	// Create stores for omni information
	const omniVersionStore = writable<string>('Loading...');
	const omniPermissionsStore = writable<
		boolean | { screenRecording: boolean; microphone: boolean } | string
	>('Loading...');

	// Load omni information when component mounts (devices are loaded via centralized store)
	Promise.all([
		getOmniVersion()
			.then((version) => omniVersionStore.set(version))
			.catch((error) =>
				omniVersionStore.set(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`)
			),
		checkPermissions(false)
			.then((hasPermissions) => {
				// Store the actual permissions object instead of converting to boolean
				omniPermissionsStore.set(hasPermissions);
			})
			.catch((error) =>
				omniPermissionsStore.set(
					`Error: ${error instanceof Error ? error.message : 'Unknown error'}`
				)
			)
	]);

	// Helper function to check if something is a Svelte store
	function isStore(obj: any): obj is Readable<any> {
		return obj && typeof obj === 'object' && 'subscribe' in obj;
	}

	// Helper function to extract store values
	function extractStoreValues(
		obj: any,
		skipFields: Set<string> = new Set(),
		seen: WeakSet<object> = new WeakSet(),
		truncateLimit: number = 50
	): any {
		if (isStore(obj)) {
			return extractStoreValues(get(obj), skipFields, seen, truncateLimit);
		}

		if (obj === null) {
			return null;
		}

		if (typeof obj !== 'object') {
			return obj;
		}

		// Prevent endlessly walking circular references or duplicate structures
		if (seen.has(obj)) {
			return '[duplicate reference]';
		}
		seen.add(obj);

		if (obj instanceof Date) {
			return obj.toISOString();
		}

		const isMap = obj instanceof Map;

		// Handle Arrays - truncate if longer than 100 items
		if (Array.isArray(obj)) {
			if (obj.length > truncateLimit) {
				return {
					_truncated: true,
					originalLength: obj.length,
					data: obj.slice(0, truncateLimit).map((item) => {
						if (isStore(item)) {
							return extractStoreValues(get(item), skipFields, seen, truncateLimit);
						} else if (typeof item === 'object' && item !== null) {
							return extractStoreValues(item, skipFields, seen, truncateLimit);
						} else {
							return item;
						}
					})
				};
			} else {
				return obj.map((item) => {
					if (isStore(item)) {
						return extractStoreValues(get(item), skipFields, seen, truncateLimit);
					} else if (typeof item === 'object' && item !== null) {
						return extractStoreValues(item, skipFields, seen, truncateLimit);
					} else {
						return item;
					}
				});
			}
		}

		// Handle Maps
		if (isMap) {
			const result: Record<string, any> = {};
			let i = 0;
			for (const [key, value] of obj.entries()) {
				if (i >= truncateLimit) break;
				i++;
				if (skipFields.has(key)) continue;
				if (isStore(value)) {
					result[key] = extractStoreValues(get(value), skipFields, seen, truncateLimit);
				} else if (typeof value === 'object' && value !== null) {
					result[key] = extractStoreValues(value, skipFields, seen, truncateLimit);
				} else {
					result[key] = value;
				}
			}
			return result;
		}

		// Handle regular objects
		const result: Record<string, any> = {};
		let i = 0;
		for (const [key, value] of Object.entries(obj)) {
			if (i >= truncateLimit) break;
			i++;
			if (skipFields.has(key)) continue;
			if (isStore(value)) {
				result[key] = extractStoreValues(get(value), skipFields, seen, truncateLimit);
			} else if (typeof value === 'object' && value !== null) {
				result[key] = extractStoreValues(value, skipFields, seen, truncateLimit);
			} else {
				result[key] = value;
			}
		}
		return result;
	}

	// Helper function to process and highlight content
	const processAndHighlight = (text: string, query: string) => {
		if (!query.trim()) return [{ text, match: false, key: '0' }];
		return highlightWords({
			text: text,
			query: query,
			matchExactly: true,
			clipBy: 8
		});
	};

	// Create debug data with store values
	const debugData = {
		config: () => extractStoreValues(ConfigStore),
		onboarding: () => extractStoreValues(OnboardingStore),
		auth: () => extractStoreValues(AuthStore),
		assistantDerived: () => extractStoreValues(assistantDerived),
		viewer: () => extractStoreValues(ViewerStore),
		viewerSmall: () => extractStoreValues(ViewerStore, new Set(['rawOutput', 'output'])),
		viewers: () => extractStoreValues(ViewersStore),
		providers: () => extractStoreValues(ProvidersStore),
		nav: () => extractStoreValues(NavigationStore),
		sidebar: () => extractStoreValues(SidebarStore),
		welcome: () => extractStoreValues(WelcomeStore),
		calendar: () => extractStoreValues(CalendarStore),
		selectedCalendars: () => extractStoreValues(SelectedCalendarsStore),
		tasks: () => extractStoreValues({ taskStore: TaskStore.taskStore }),
		uiTasks: () => extractStoreValues(FilteredTasksStores),
		draftFlow: () => extractStoreValues(DraftFlowStores),
		workspaces: () => extractStoreValues({ workspaceStore }),
		research: () => extractStoreValues(ResearchStore),
		chat: () => extractStoreValues({ chatServiceHistory }),
		devices: () => extractStoreValues(DevicesStore),
		lastUsedDevice: () => extractStoreValues(LastUsedDeviceStore),
		threads: () => extractStoreValues(ThreadsStore),
		storedCalendarEvents: () => extractStoreValues(CalendarEventStore),
		conversations: () => extractStoreValues(ConversationStore),
		currentDraft: () => extractStoreValues(currentDraft),
		linearProvider: () => extractStoreValues(LinearProviderStore),
		exportLinearDialog: () => extractStoreValues(ExportLinearDialogStore),
		emailContacts: () => {
			const contacts = get(emailContacts);
			return {
				contacts: contacts,
				count: contacts.length,
				sampleContacts: contacts.slice(0, 5)
			};
		},
		taskSynthesis: () => extractStoreValues(TaskSynthStore),
		// NEW: UI Calendar Stores for debugging our new architecture
		'ui-calendar-events': () => {
			const events = get(uiCalendarEvents);
			return {
				events: events,
				count: events.length,
				statistics: getEventStatistics(events),
				sampleEvents: events.slice(0, 3) // Show first 3 events for inspection
			};
		},
		'ui-calendar-by-date': () => get(uiCalendarEventsByDate),
		'ui-calendar-by-section': () => get(uiCalendarEventsBySection),
		'ui-calendar-store-state': () => get(uiCalendarStoreState),
		'ui-calendar-overview': () => get(uiCalendarOverviewEvents),
		'ui-calendar-week': () => {
			const weekEvents = get(uiCalendarWeekEvents);
			return {
				events: weekEvents,
				count: weekEvents.length,
				sampleEvents: weekEvents.slice(0, 3) // Show first 3 for inspection
			};
		},
		'ui-calendar-month': () => {
			const monthEvents = get(uiCalendarMonthEvents);
			return {
				events: monthEvents,
				count: monthEvents.length,
				sampleEvents: monthEvents.slice(0, 3)
			};
		},
		'ui-calendar-day': () => {
			const dayEvents = get(uiCalendarDayEvents);
			return {
				events: dayEvents,
				count: dayEvents.length
			};
		},
		'ui-calendar-nav': () => {
			const navEvents = get(uiCalendarNavEvents);
			return {
				events: navEvents,
				count: navEvents.length,
				sampleEvents: navEvents.slice(0, 5).map((e) => ({
					title: e.title,
					start: e.start.toISOString(),
					isUpcoming: e.isUpcoming,
					source: e.source
				}))
			};
		},

		transcripts: () =>
			get(recordedTranscripts)
				.map(([id, transcript]) => ({
					id,
					name: transcript.name,
					transcriptName: getTranscriptName(transcript),
					editedName: transcript.editedName,
					typeEditedName: typeof transcript.editedName,
					summary: transcript.summary,
					status: transcript.status,
					duration: transcript.duration,
					eventId: transcript.eventId,
					fileExists: transcript.fileExists,
					file: transcript.file,
					workspaceId: transcript.workspaceId,
					isDeleted: transcript.isDeleted
				}))
				.slice(0, 10),

		transcriptsView: () => get(transcriptsView),

		transcriptOutput: () => get(transcriptOutput),

		omni: () => {
			const version = get(omniVersionStore);
			const permissions = get(omniPermissionsStore);
			const devicesStoreData = get(DevicesStore.devices);

			// Get devices from centralized store
			let devicesObj: any = devicesStoreData.deviceNameMap || 'Not loaded';
			if (devicesStoreData.deviceNameMap instanceof Map) {
				devicesObj = Object.fromEntries(devicesStoreData.deviceNameMap);
			}

			return {
				version,
				devices: devicesObj,
				deviceCount:
					devicesStoreData.deviceNameMap instanceof Map ? devicesStoreData.deviceNameMap.size : 0,
				selectedDevice: devicesStoreData.selectedDevice,
				permissions,
				status: version.startsWith('Error') ? 'error' : 'available',
				lastUpdated: new Date().toISOString()
			};
		}
	};

	// Get the currently selected data
	let currentData = $derived(debugData[selectedData as keyof typeof debugData]());

	// Filter debug data based on search query
	let filteredData = $derived(
		(() => {
			if (!searchQuery.trim()) return currentData;

			// Convert data to searchable string
			const dataString = JSON.stringify(currentData, null, 2).toLowerCase();
			const query = searchQuery.toLowerCase();

			// Simple search - if query is found in the data, show all data
			// For more sophisticated filtering, we could filter individual properties
			if (dataString.includes(query)) {
				return currentData;
			}

			return { message: `No results found for "${searchQuery}"` };
		})()
	);

	// Generate search context for better debugging
	let searchContext = $derived(
		(() => {
			if (!searchQuery.trim() || !currentData) return null;

			const dataString = JSON.stringify(currentData, null, 2);
			const query = searchQuery.toLowerCase();

			// Find all occurrences with context
			const lines = dataString.split('\n');
			const matches: Array<{
				lineNumber: number;
				content: string;
				context: string[];
			}> = [];

			lines.forEach((line, index) => {
				if (line.toLowerCase().includes(query)) {
					matches.push({
						lineNumber: index + 1,
						content: line.trim(),
						context: lines.slice(Math.max(0, index - 1), index + 2).map((l) => l.trim())
					});
				}
			});

			return matches.slice(0, 10); // Limit to 10 matches
		})()
	);

	// Filter store options based on search
	let filteredStoreOptions = $derived(
		(() => {
			if (!searchQuery.trim()) return Object.keys(debugData);

			const query = searchQuery.toLowerCase();
			return Object.keys(debugData).filter(
				(key) =>
					key.toLowerCase().includes(query) ||
					key.replace(/[-_]/g, ' ').toLowerCase().includes(query)
			);
		})()
	);
</script>

<div
	class="fixed bottom-4 right-4 z-[901] opacity-0 transition-opacity duration-200 hover:opacity-100"
>
	<Button
		extraClass="p-2 antialiased bg-background ring-0 border-foreground text-foreground rounded-full hover:bg-background/80 hover:border-foreground/40 hover:text-foreground/80 border border-2 border-foreground/20"
		onclick={() => (showDebugPanel = !showDebugPanel)}
	>
		<Hammer class="size-4" />
	</Button>
</div>

{#if showDebugPanel}
	<div
		class="prose leading-0 dark:prose-invert prose-li:list-none fixed inset-0 z-[900] overflow-auto bg-black bg-opacity-75 p-4 text-sm"
	>
		<div class="mx-auto max-w-6xl rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900">
			<div class="mb-4 flex items-center justify-between">
				<h3 class="text-xl font-semibold text-gray-900 dark:text-white">
					Debug Panel
					{#if searchQuery.trim()}
						<span class="ml-2 text-sm font-normal text-gray-500">- Searching: "{searchQuery}"</span>
					{/if}
				</h3>
				<Button variant="secondary" onclick={() => (showDebugPanel = false)}>Close</Button>
			</div>

			<!-- Search Input -->
			<div class="mb-4">
				<input
					type="text"
					bind:value={searchQuery}
					placeholder="Search stores or data content..."
					class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400"
				/>
			</div>

			<!-- Store Selector -->
			<div class="mb-4">
				<select
					bind:value={selectedData}
					class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400"
				>
					{#each filteredStoreOptions as key (key)}
						<option value={key}>
							{key}
							{#if key.startsWith('ui-calendar')}
								🆕
							{/if}
						</option>
					{/each}
				</select>
				{#if filteredStoreOptions.length === 0}
					<p class="mt-2 text-sm text-gray-500">No stores match your search</p>
				{/if}
			</div>

			<!-- Store Statistics for UI Calendar Stores -->
			{#if selectedData.startsWith('ui-calendar') && currentData && typeof currentData === 'object'}
				<div class="mb-4 rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
					<h4 class="mb-2 font-medium text-green-800 dark:text-green-200">
						🆕 UI Calendar Store Debug Info
					</h4>
					<div class="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
						{#if 'count' in currentData}
							<div>
								<span class="font-medium">Count:</span>
								<span class="ml-1 text-green-700 dark:text-green-300">{currentData.count}</span>
							</div>
						{/if}
						{#if 'statistics' in currentData && currentData.statistics}
							<div>
								<span class="font-medium">By Source:</span>
								<span class="ml-1 text-green-700 dark:text-green-300">
									📅 {currentData.statistics.bySource.calendar} | 🎙️ {currentData.statistics
										.bySource.recording}
								</span>
							</div>
							<div>
								<span class="font-medium">With Summary:</span>
								<span class="ml-1 text-green-700 dark:text-green-300"
									>{currentData.statistics.byStatus.withSummary}</span
								>
							</div>
							<div>
								<span class="font-medium">Processing:</span>
								<span class="ml-1 text-green-700 dark:text-green-300"
									>{currentData.statistics.byStatus.processing}</span
								>
							</div>
						{/if}
					</div>
				</div>
			{/if}

			<!-- Search Context -->
			{#if searchQuery.trim() && searchContext && searchContext.length > 0}
				<div class="mb-4 rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
					<h4 class="mb-2 font-medium text-blue-800 dark:text-blue-200">
						🔍 Search Results ({searchContext.length} matches found)
					</h4>
					<div class="space-y-2 text-sm">
						{#each searchContext as match (match.lineNumber)}
							<div
								class="rounded border border-blue-200 bg-white p-2 dark:border-blue-700 dark:bg-gray-800"
							>
								<div class="font-mono text-xs text-gray-500">Line {match.lineNumber}</div>
								<div class="font-mono text-sm">
									{#each processAndHighlight(match.content, searchQuery) as chunk (chunk.key)}
										<span class={chunk.match ? 'border-b border-yellow-400 bg-yellow-200' : ''}
											>{chunk.text}</span
										>
									{/each}
								</div>
							</div>
						{/each}
					</div>
				</div>
			{/if}

			<!-- Data Display -->
			<div class="data-display select-text space-y-4 text-gray-900 dark:text-gray-100">
				<JsonView json={filteredData} depth={4} />
			</div>

			<!-- Quick Access to UI Calendar Stores -->
			<div class="mt-6 border-t pt-4">
				<h4 class="mb-3 font-medium text-gray-900 dark:text-white">
					🆕 Quick Access - UI Calendar Stores
				</h4>
				<div class="grid grid-cols-2 gap-2 md:grid-cols-4">
					{#each Object.keys(debugData).filter( (key) => key.startsWith('ui-calendar') ) as store (store)}
						<button
							onclick={() => (selectedData = store)}
							class="rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800 hover:bg-green-100 dark:border-green-600 dark:bg-green-900/20 dark:text-green-200 dark:hover:bg-green-900/40"
							class:ring-2={selectedData === store}
							class:ring-green-500={selectedData === store}
						>
							{store.replace('ui-calendar-', '').replace('-', ' ')}
						</button>
					{/each}
				</div>
			</div>

			<!-- Manual Memory/Transcript Creator -->
			<div class="mt-6 border-t pt-4">
				<h4 class="mb-3 font-medium text-gray-900 dark:text-white">
					📝 Create Manual Memory from VTT
				</h4>
				<Button
					onclick={() => (showVttModal = true)}
					extraClass="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-800 hover:bg-blue-100 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-200 dark:hover:bg-blue-900/40"
				>
					Create from VTT Format
				</Button>
				<p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
					Paste WebVTT format transcript to create a manual memory object
				</p>
			</div>
		</div>
	</div>
{/if}

<!-- VTT Input Modal -->
{#if showVttModal}
	<div
		class="prose leading-0 dark:prose-invert prose-li:list-none fixed inset-0 z-[901] overflow-auto bg-black bg-opacity-75 p-4 text-sm"
	>
		<div class="mx-auto max-w-2xl rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900">
			<div class="mb-4 flex items-center justify-between">
				<h3 class="text-xl font-semibold text-gray-900 dark:text-white">Create Memory from VTT</h3>
				<Button
					variant="secondary"
					onclick={() => {
						showVttModal = false;
						vttError = '';
					}}
				>
					Close
				</Button>
			</div>

			<div class="mb-4">
				<label
					for="vttInput"
					class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
				>
					VTT Format (WebVTT)
				</label>
				<textarea
					bind:value={vttInput}
					placeholder={`WEBVTT

00:00:02.464 --> 00:00:06.344
I am a CEO of a healthcare company.

00:00:06.344 --> 00:00:07.464
I have been in the healthcare industry for over 20 years.`}
					class="h-64 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-400"
				></textarea>
			</div>

			{#if vttError}
				<div
					class="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-600 dark:bg-red-900/20 dark:text-red-200"
				>
					{vttError}
				</div>
			{/if}

			<div class="flex gap-2">
				<Button
					onclick={handleVttSubmit}
					extraClass="rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-200 dark:hover:bg-blue-900/40"
				>
					Create Memory
				</Button>
				<Button
					variant="secondary"
					onclick={() => {
						showVttModal = false;
						vttError = '';
					}}
				>
					Cancel
				</Button>
			</div>

			<div
				class="mt-4 rounded-md bg-gray-50 p-3 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400"
			>
				<p class="font-medium">Supported formats:</p>
				<ul class="mt-2 list-inside list-disc space-y-1">
					<li><strong>Standard WebVTT:</strong> Text on new line after timestamp</li>
					<li>
						<strong>Dash-separated:</strong>
						<code>00:00:02.464 --> 00:00:06.344- Text content</code>
					</li>
					<li>
						Optional speaker prefix: <code>Speaker Name: text</code> or <code>Jane - text</code>
					</li>
					<li>Multiple text lines are joined with spaces</li>
				</ul>
			</div>
		</div>
	</div>
{/if}

<style lang="postcss">
	:global(.data-display ul) {
		@apply pl-4;
	}
</style>
